package diana.soleil.design.iraniancanadienbazzar.feature.business

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.common.Formatters
import diana.soleil.design.iraniancanadienbazzar.core.common.SearchMatching
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CategoryRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.FavoritesController
import diana.soleil.design.iraniancanadienbazzar.core.data.LocationController
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessAttributeRegistry
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import diana.soleil.design.iraniancanadienbazzar.core.model.DiscoveryRanking
import diana.soleil.design.iraniancanadienbazzar.core.model.GeoPoint
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.flow.stateIn

/** How results are ordered. Recommended keeps sponsored first (the directory's default ranking). */
enum class SearchSort { Recommended, Nearest, TopRated, MostReviewed, NameAsc }

/**
 * Directory filters/sort applied client-side over the metro-scoped corpus. [minRating] is a floor
 * (0 = any). [activeCount] powers the badge on the filter button — sort is not counted since there is
 * always a sort.
 */
data class SearchFilters(
    val sort: SearchSort = SearchSort.Recommended,
    val minRating: Int = 0,
    val verifiedOnly: Boolean = false,
    val sponsoredOnly: Boolean = false,
    /** Structured facts every result must declare (BusinessAttributeRegistry.popularFilters). */
    val attributes: Set<String> = emptySet(),
) {
    val activeCount: Int get() = listOf(minRating > 0, verifiedOnly, sponsoredOnly).count { it } + attributes.size
}

data class SearchUiState(
    val query: String = "",
    val filters: SearchFilters = SearchFilters(),
    val loading: Boolean = true,
    val error: Boolean = false,
    val results: List<Business> = emptyList(),
    val favorites: Set<String> = emptySet(),
    /** True once the user has typed a query or set a filter — distinguishes "start typing" from "no matches". */
    val hasQueryOrFilter: Boolean = false,
    /** Recent queries (newest first) shown on the pre-typing screen; empty = section hidden. */
    val recentSearches: List<String> = emptyList(),
    /** Category shortcuts for the pre-typing screen (browse is the other way in). */
    val categories: List<Category> = emptyList(),
)

/**
 * Full-directory search: streams the approved businesses for the active metro from [BusinessRepository]
 * and applies the text query + [SearchFilters] reactively (re-runs on metro change, query, filters, and
 * favorites). Matching is language-agnostic — it scans every `LocalizedText` variant (en/fr/fa) plus the
 * canonical name, category and city — so results are found regardless of the active UI language.
 *
 * Firestore has no native full-text search; client-side filtering over the metro corpus is the pragmatic
 * MVP (the metro scope keeps the set small). A server index (e.g. Algolia/Typesense) is a later upgrade.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class SearchViewModel(
    private val businessRepository: BusinessRepository,
    cityController: CityController,
    private val favoritesController: FavoritesController,
    private val analytics: AnalyticsTracker,
    categoryRepository: CategoryRepository,
    private val preferences: AppPreferences,
    locationController: LocationController,
) : ViewModel() {

    private val queryFlow = MutableStateFlow("")
    private val filtersFlow = MutableStateFlow(SearchFilters())
    private val recentFlow = MutableStateFlow(preferences.recentSearches)

    /**
     * KPI: a search that converted — the user opened a result. Logged with the query length (not the
     * text — low-noise, no PII) so we can tell short lookups from exploratory searches. A converted
     * query is also the signal it's worth remembering on the pre-typing screen.
     */
    fun trackResultClick(businessId: String) {
        val query = queryFlow.value.trim()
        if (query.isNotBlank()) {
            val next = (listOf(query) + recentFlow.value).distinct()
            preferences.recentSearches = next
            recentFlow.value = preferences.recentSearches
        }
        analytics.track(
            AnalyticsEvents.SEARCH_RESULT_CLICK,
            mapOf(
                AnalyticsParams.BUSINESS_ID to businessId,
                AnalyticsParams.QUERY_LENGTH to query.length,
            ),
        )
    }

    fun clearRecentSearches() {
        preferences.recentSearches = emptyList()
        recentFlow.value = emptyList()
    }

    private val corpus: Flow<DataResult<List<Business>>> =
        combine(cityController.activeMetroId, cityController.activeCountry) { m, c -> m to c }
            .flatMapLatest { (metroId, country) ->
                businessRepository.observeBusinesses(metroId = metroId, country = country.code)
            }

    // Where "nearby"/"Nearest" measures from: GPS fix when granted, else the active metro's centre.
    private val originFlow: Flow<GeoPoint?> =
        combine(locationController.location, cityController.activeMetroId, DiscoveryRanking::origin)

    // Pre-typing content: recent queries + category shortcuts (a category failure just hides the
    // section — it must never error the search screen).
    private val prelude: Flow<Pair<List<String>, List<Category>>> =
        combine(recentFlow, categoryRepository.observeCategories()) { recents, categories ->
            recents to ((categories as? DataResult.Success)?.data ?: emptyList())
        }

    /**
     * Ranked results computed off the MAIN thread from a debounced query: per-keystroke
     * normalize+filter+distance-sort of the whole metro corpus is imperceptible today but janks
     * low-end devices as a metro grows. The text field echoes [queryFlow] directly, so typing
     * stays instant while results settle ~200ms behind.
     */
    @OptIn(kotlinx.coroutines.FlowPreview::class)
    private val rankedResults: Flow<Pair<DataResult<List<Business>>, GeoPoint?>> =
        combine(
            combine(corpus, originFlow) { result, origin -> result to origin },
            queryFlow.debounce(200),
            filtersFlow,
        ) { (result, origin), query, filters ->
            when (result) {
                is DataResult.Success -> {
                    val ranked = result.data.applySearch(query, filters, origin)
                    // One event per settled (debounced) non-blank query — the "did anyone search,
                    // and did they find anything" number the weekly review needs.
                    if (query.isNotBlank()) {
                        analytics.track(
                            AnalyticsEvents.SEARCH_PERFORMED,
                            mapOf(
                                AnalyticsParams.QUERY_LENGTH to query.trim().length,
                                AnalyticsParams.RESULT_COUNT to ranked.size,
                            ),
                        )
                    }
                    DataResult.Success(ranked) to origin
                }
                is DataResult.Failure -> result to origin
            }
        }.flowOn(Dispatchers.Default)

    val state: StateFlow<SearchUiState> =
        combine(
            rankedResults,
            queryFlow,
            filtersFlow,
            favoritesController.favorites,
            prelude,
        ) { (result, _), query, filters, favorites, (recents, categories) ->
            val hasQueryOrFilter = query.isNotBlank() || filters.activeCount > 0
            when (result) {
                is DataResult.Success -> SearchUiState(
                    query = query,
                    filters = filters,
                    loading = false,
                    results = result.data,
                    favorites = favorites,
                    hasQueryOrFilter = hasQueryOrFilter,
                    recentSearches = recents,
                    categories = categories,
                )
                is DataResult.Failure -> SearchUiState(
                    query = query,
                    filters = filters,
                    loading = false,
                    error = true,
                    favorites = favorites,
                    hasQueryOrFilter = hasQueryOrFilter,
                    recentSearches = recents,
                    categories = categories,
                )
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), SearchUiState())

    fun onQueryChange(query: String) { queryFlow.value = query }
    fun onFiltersChange(filters: SearchFilters) { filtersFlow.value = filters }
    fun clearFilters() { filtersFlow.value = SearchFilters() }
    fun toggleFavorite(businessId: String) { favoritesController.toggle(businessId) }
}

private fun List<Business>.applySearch(query: String, filters: SearchFilters, origin: GeoPoint?): List<Business> {
    val normalizedQuery = Formatters.foldForSearch(query).trim()
    val tokens = normalizedQuery.split(' ').filter { it.isNotBlank() }
    return asSequence()
        .filter { business ->
            (!filters.verifiedOnly || business.verified) &&
                (!filters.sponsoredOnly || business.sponsorship.active) &&
                (business.rating >= filters.minRating) &&
                filters.attributes.all { it in business.attributes } &&
                // Forgiving match: accents already folded, plus one or two letters of slack per
                // word so "Chateau Kebab" finds "Château Kabab" (QA 2026-09-12).
                (tokens.isEmpty() || business.searchHaystack().let { hay -> tokens.all { SearchMatching.matches(hay, it) } })
        }
        .sortedWith(filters.sort.comparator(origin))
        .toList()
}

/** Lowercased bag of every searchable field, across all language variants. */
private fun Business.searchHaystack(): String = buildList {
    add(name)
    nameLocalized?.let { addAll(listOfNotNull(it.en, it.fr, it.fa)) }
    serviceTitle?.let { addAll(listOfNotNull(it.en, it.fr, it.fa)) }
    add(categoryId)
    // Structured facts are text-searchable too: typing "halal" finds businesses declaring it.
    BusinessAttributeRegistry.resolve(categoryId, attributes).forEach { def ->
        addAll(listOfNotNull(def.label.en, def.label.fr, def.label.fa))
    }
    add(address.city)
}.joinToString(" ").let(Formatters::foldForSearch)

private fun SearchSort.comparator(origin: GeoPoint?): Comparator<Business> = when (this) {
    // Sponsored placements stay first, then the shared appeal ranking (photo → nearby → rating).
    SearchSort.Recommended ->
        compareByDescending<Business> { it.sponsorship.active }
            .then(DiscoveryRanking.businessComparator(origin))
    SearchSort.Nearest ->
        DiscoveryRanking.businessNearestComparator(origin)
    SearchSort.TopRated ->
        compareByDescending<Business> { it.rating }.thenByDescending { it.reviewCount }
    SearchSort.MostReviewed ->
        compareByDescending<Business> { it.reviewCount }.thenByDescending { it.rating }
    SearchSort.NameAsc ->
        compareBy { it.name.lowercase() }
}
